import styled, { DefaultTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 32px;
  min-height: 100vh;
  background: ${(props) => getThemeValue(props, '#0f172a', '#f8fafc')};
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  transition: background 0.3s ease;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 32px;
  gap: 20px;
  flex-wrap: wrap;
`;

export const BackButton = styled.button`
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  border-radius: 12px;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#475569')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    background: ${(props) => getThemeValue(props, '#334155', '#f1f5f9')};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${(props) => getThemeValue(props, '#475569', '#cbd5e1')};
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const Subtitle = styled.p`
  font-size: 15px;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  font-weight: 400;
  line-height: 1.6;
`;

export const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

export const FilterLabel = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  font-weight: 500;
`;

export const SalesSummaryCard = styled.div`
  padding: 32px;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const SummaryTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  letter-spacing: -0.3px;
`;

export const SummaryDescription = styled.p`
  font-size: 14px;
  margin: 0 0 24px 0;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  line-height: 1.6;
`;

export const TotalSalesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

export const TotalSalesAmount = styled.div`
  font-size: 42px;
  font-weight: 800;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  letter-spacing: -1px;
  background: linear-gradient(
    135deg,
    ${(props) => getThemeValue(props, '#60a5fa', '#3b82f6')} 0%,
    ${(props) => getThemeValue(props, '#a78bfa', '#8b5cf6')} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

export const TrendIndicator = styled.div<{ isIncrease: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => (props.isIncrease ? '#10b981' : '#ef4444')};
  font-size: 14px;
  font-weight: 600;
  padding: 6px 12px;
  background: ${(props) =>
    props.isIncrease
      ? getThemeValue(props, 'rgba(16, 185, 129, 0.1)', 'rgba(16, 185, 129, 0.1)')
      : getThemeValue(props, 'rgba(239, 68, 68, 0.1)', 'rgba(239, 68, 68, 0.1)')};
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.isIncrease
        ? getThemeValue(props, 'rgba(16, 185, 129, 0.2)', 'rgba(16, 185, 129, 0.2)')
        : getThemeValue(props, 'rgba(239, 68, 68, 0.2)', 'rgba(239, 68, 68, 0.2)')};
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const MetricCard = styled.div`
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  border-radius: 12px;
  padding: 20px;
  background: ${(props) => getThemeValue(props, '#0f172a', '#ffffff')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border-color: ${(props) => getThemeValue(props, '#475569', '#cbd5e1')};

    &::before {
      opacity: 1;
    }
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const MetricIcon = styled(FontAwesomeIcon)<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 22px;
  padding: 8px;
  background: ${(props) => `${props.color}15`};
  border-radius: 8px;
  transition: transform 0.3s ease;

  ${MetricCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

export const MetricTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#cbd5e1', '#475569')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  margin-top: 8px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const ChartCard = styled.div`
  border-radius: 16px;
  width: 100%;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

export const ChartControls = styled.div`
  padding: 24px;
  display: flex;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#0f172a', '#f8fafc')};
`;

export const ChartMetrics = styled.div`
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;
  background: ${(props) => getThemeValue(props, '#0f172a', '#f8fafc')};
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};

  @media (max-width: 768px) {
    gap: 16px;
    padding: 20px;
  }
`;

export const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  padding: 8px 16px;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => getThemeValue(props, '#334155', '#f1f5f9')};
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }
`;

export const MetricIconSmall = styled(FontAwesomeIcon)<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 16px;
`;

export const MetricText = styled.span`
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  font-weight: 600;
  font-size: 15px;
`;

export const ChartContainer = styled.div`
  width: 100%;
  height: 380px;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0f172a', '#ffffff')};

  @media (max-width: 768px) {
    height: 300px;
    padding: 16px;
  }
`;

export const TableCard = styled.div`
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#1e293b', '#ffffff')};
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-top: 32px;
  width: 100%;
  border: 1px solid ${(props) => getThemeValue(props, '#334155', '#e2e8f0')};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const TableHeader = styled.div`
  margin-bottom: 24px;
`;

export const TableTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  letter-spacing: -0.3px;
`;

export const TableDescription = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  line-height: 1.6;
`;

export const DateSelectorWrapper = styled.div`
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
`;

export const MetricsSection = styled.div`
  padding-bottom: 32px;
`;

export const LoadingContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: ${(props) => getThemeValue(props, '#94a3b8', '#64748b')};
  font-size: 14px;
  font-weight: 500;
`;

export const KeyMetricsTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
  color: ${(props) => getThemeValue(props, '#f1f5f9', '#0f172a')};
  letter-spacing: -0.3px;
`;
